import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "../api/Api.js";
import {
  LogIn,
  UserCheck,
  UserX,
  PlayCircle,
  StopCircle,
  AlertTriangle,
  Clock,
} from "lucide-react";
import BrandLogo from "../assets/Logo Patra Travels (Full White).png";

const DashboardContent = () => {
  const [time, setTime] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initial State
  const [stats, setStats] = useState({
    attendanceToday: 0,
    attendanceOutToday: 0,
    loginToday: 0,
    dutyToday: 0,
    dutyOutToday: 0,
    sos: {
      total: 0,
      driverTotal: 0,
      customerTotal: 0,
      driverSolved: 0,
      customerSolved: 0,
    },
  });

  // --- API FETCH LOGIC ---
  const fetchDashboardStats = async () => {
    try {
      const response = await api.get("/dashboard/daily-stats");
      if (response.data) {
        setStats(response.data);
        setError(null);
      }
    } catch (err) {
      console.error("Dashboard API Error:", err);
      setError("Offline");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardStats();
    const intervalId = setInterval(fetchDashboardStats, 30000);
    return () => clearInterval(intervalId);
  }, []);

  // --- DATE FORMATTING ---
  const dateString = time.toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "short",
  });

  // --- SOS CALCULATIONS ---
  const solvedCount =
    Number(stats.sos.driverSolved || 0) + Number(stats.sos.customerSolved || 0);
  const rawTotal = Number(stats.sos.total || 0);
  let pendingCount = 0;
  let displayTotal = 0;

  if (rawTotal < solvedCount) {
    pendingCount = rawTotal;
    displayTotal = pendingCount + solvedCount;
  } else {
    pendingCount = rawTotal - solvedCount;
    displayTotal = rawTotal;
  }

  const percentPending =
    displayTotal > 0 ? (pendingCount / displayTotal) * 100 : 0;
  const percentSolved =
    displayTotal > 0 ? (solvedCount / displayTotal) * 100 : 0;

  return (
    <div
      className="container-fluid"
      style={{ backgroundColor: "#ffffff", minHeight: "100vh" }}
    >
      {/* ================= 1. COMPACT HEADER SECTION ================= */}
      <div className="row g-3 mb-4">
        {/* WELCOME BANNER (Col-8) */}
        <div className="col-12 col-xl-8">
          <div
            className="card border-0 shadow-sm rounded-4 overflow-hidden position-relative justify-content-center"
            style={{
              background: "linear-gradient(135deg, #0f172a 0%, #334155 100%)",
              minHeight: "120px", // Compact Height
            }}
          >
            {/* Background Effects */}
            <div
              className="position-absolute top-0 end-0 bg-primary opacity-25 rounded-circle blur-xl"
              style={{
                width: "150px",
                height: "150px",
                transform: "translate(30%, -30%)",
                filter: "blur(40px)",
              }}
            ></div>

            <div className="card-body px-4 py-3 d-flex align-items-center justify-content-between position-relative z-2">
              <div className="d-flex flex-column justify-content-center text-white">
                <div className="d-flex align-items-center mb-1">
                  <span
                    className="badge bg-white bg-opacity-10 border border-white border-opacity-25 rounded-pill px-2 py-1 fw-normal text-white"
                    style={{ fontSize: "0.7rem" }}
                  >
                    Admin Panel
                  </span>
                  <span
                    className="text-white text-opacity-75 ms-3 small d-flex align-items-center fw-medium"
                    style={{ fontSize: "0.8rem" }}
                  >
                    <Clock size={12} className="me-1" /> {dateString}
                  </span>
                </div>
                <h3 className="mb-0 fw-bold">
                  Welcome, <span className="text-warning">Admin</span>
                </h3>
              </div>

              {/* Brand Logo */}
              <div className="d-none d-md-block opacity-75">
                <img
                  src={BrandLogo}
                  alt="Logo"
                  style={{ height: "42px", filter: "brightness(0) invert(1)" }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* SOS ALERTS CARD (Col-4) - Compact Horizontal Layout */}
        <div className="col-12 col-xl-4">
          <Link to="/sos" className="text-decoration-none">
            <div
              className="card border-0 shadow-sm rounded-4 overflow-hidden hover-lift position-relative"
              style={{
                background: "linear-gradient(135deg, #e97d7d 0%, #991b1b 100%)",
                minHeight: "120px", // Matches Welcome Card
              }}
            >
              <div className="card-body p-3 d-flex align-items-center justify-content-between text-white position-relative z-1 h-100">
                {/* Left: Icon & Text */}
                <div className="d-flex align-items-center">
                  <div
                    className={`p-3 rounded-circle me-3 ${pendingCount > 0 ? "bg-white text-danger animate-pulse" : "bg-danger bg-opacity-20 text-white"}`}
                  >
                    <AlertTriangle size={28} />
                  </div>
                  <div>
                    <h6
                      className="text-uppercase fw-bold text-white text-opacity-90 mb-1 ls-1"
                      style={{ fontSize: "0.85rem" }}
                    >
                      SOS Alerts
                    </h6>
                    <div className="d-flex align-items-center">
                      <span
                        className="badge bg-black bg-opacity-25 rounded-pill me-2 fw-normal"
                        style={{ fontSize: "0.7rem" }}
                      >
                        {pendingCount} Pending
                      </span>
                      <span
                        className="badge bg-white bg-opacity-25 rounded-pill fw-normal"
                        style={{ fontSize: "0.7rem" }}
                      >
                        {solvedCount} Solved
                      </span>
                    </div>
                  </div>
                </div>

                {/* Right: Big Number */}
                <div className="text-end">
                  <h1 className="fw-black mb-0 display-5 lh-1">{rawTotal}</h1>
                </div>
              </div>
            </div>
          </Link>
        </div>
      </div>

      {/* ================= 2. DRIVER MODULE STATS (5 CARDS) ================= */}
      <div className="mb-4">
        {/* Section Header */}
        <div className="d-flex align-items-center mb-3">
          <div
            className="bg-primary rounded-pill me-2"
            style={{ width: "4px", height: "24px" }}
          ></div>
          <h6 className="text-uppercase fw-bold text-dark mb-0 ls-1">
            Driver Activity Overview
          </h6>
        </div>

        {/* 5-COLUMN GRID */}
        <div className="row g-3 row-cols-2 row-cols-md-3 row-cols-xl-5">
          {/* 1. Active Logins */}
          <div className="col">
            <Link to="/driver-login-status" className="text-decoration-none">
              <div
                className="card h-100 border-0 shadow-sm rounded-4 hover-lift"
                style={{
                  background:
                    "linear-gradient(135deg, #3db8ff 0%, #5a52ff 100%)",
                }}
              >
                <div className="card-body p-3">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div className="bg-white rounded-circle p-2 text-info shadow-sm">
                      <LogIn size={20} />
                    </div>
                    <span className="badge bg-white text-info rounded-pill small">
                      LIVE
                    </span>
                  </div>
                  <h3 className="fw-bolder text-white mb-0">
                    {loading ? "..." : stats.loginToday}
                  </h3>
                  <small className="text-white fw-bold text-uppercase">
                    Active Logins
                  </small>
                </div>
              </div>
            </Link>
          </div>

          {/* 2. Punched In */}
          <div className="col">
            <Link to="/driverAttendance/daily" className="text-decoration-none">
              <div
                className="card h-100 border-0 shadow-sm rounded-4 hover-lift"
                style={{
                  background:
                    "linear-gradient(135deg, #27e1ae 0%, #169d60 100%)",
                }}
              >
                <div className="card-body p-3">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div className="bg-white rounded-circle p-2 text-success shadow-sm">
                      <UserCheck size={20} />
                    </div>
                    <span className="badge bg-white text-success rounded-pill small">
                      IN
                    </span>
                  </div>
                  <h3 className="fw-bolder text-white mb-0">
                    {loading ? "..." : stats.attendanceToday}
                  </h3>
                  <small className="text-white fw-bold text-uppercase">
                    Punched In
                  </small>
                </div>
              </div>
            </Link>
          </div>

          {/* 3. On Duty */}
          <div className="col">
            <Link
              to="/driver-login-status?filter=DutyStart"
              className="text-decoration-none"
            >
              <div
                className="card h-100 border-0 shadow-sm rounded-4 hover-lift"
                style={{
                  background:
                    "linear-gradient(135deg, #ffae2f 0%, #d86d1e 100%)",
                }}
              >
                <div className="card-body p-3">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div className="bg-white rounded-circle p-2 text-warning shadow-sm">
                      <PlayCircle size={20} />
                    </div>
                    <span className="badge bg-white text-warning rounded-pill small">
                      RUN
                    </span>
                  </div>
                  <h3 className="fw-bolder text-white mb-0">
                    {loading ? "..." : stats.dutyToday}
                  </h3>
                  <small className="text-white fw-bold text-uppercase">
                    On Duty
                  </small>
                </div>
              </div>
            </Link>
          </div>

          {/* 4. Duty Completed */}
          <div className="col">
            <Link
              to="/driver-login-status?filter=DutyEnd"
              className="text-decoration-none"
            >
              <div
                className="card h-100 border-0 shadow-sm rounded-4 hover-lift"
                style={{
                  background:
                    "linear-gradient(135deg, #a565ff 0%, #8a3cff 100%)",
                }}
              >
                <div className="card-body p-3">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div className="bg-white rounded-circle p-2 text-primary shadow-sm">
                      <StopCircle size={20} />
                    </div>
                    <span className="badge bg-white text-primary rounded-pill small">
                      DONE
                    </span>
                  </div>
                  <h3 className="fw-bolder text-white mb-0">
                    {loading ? "..." : stats.dutyOutToday}
                  </h3>
                  <small className="text-white fw-bold text-uppercase">
                    Duty Completed
                  </small>
                </div>
              </div>
            </Link>
          </div>

          {/* 5. Punched Out */}
          <div className="col">
            <Link to="/driverAttendance/daily" className="text-decoration-none">
              <div
                className="card h-100 border-0 shadow-sm rounded-4 hover-lift"
                style={{
                  background:
                    "linear-gradient(135deg, #f05dcb 0%, #ff3247 100%)",
                }}
              >
                <div className="card-body p-3">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div className="bg-white rounded-circle p-2 text-danger shadow-sm">
                      <UserX size={20} />
                    </div>
                    <span className="badge bg-white text-danger rounded-pill small">
                      OUT
                    </span>
                  </div>
                  <h3 className="fw-bolder text-white mb-0">
                    {loading ? "..." : stats.attendanceOutToday}
                  </h3>
                  <small className="text-white fw-bold text-uppercase">
                    Punched Out
                  </small>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </div>

      {/* --- STYLES --- */}
      <style>{`
        .fw-black { font-weight: 900; }
        .ls-1 { letter-spacing: 1px; }
        .hover-lift { transition: transform 0.2s ease, box-shadow 0.2s ease; }
        .hover-lift:hover { transform: translateY(-5px); box-shadow: 0 10px 30px rgba(0,0,0,0.1) !important; }
        .animate-pulse { animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite; }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: .5; } }
      `}</style>
    </div>
  );
};

export default DashboardContent;